/**
 * Pure responsive LAYOUT math — no Phaser, no DOM. The geometry every scene uses
 * to adapt phone <-> desktop lives here as plain functions so it is unit-testable
 * and deterministic. The thin `Layout` wrapper (./Layout.ts) feeds these the live
 * viewport size + safe-area insets each frame.
 *
 * Cross-platform/touch is a HARD requirement: every placement respects the device
 * safe-area (notch / home-indicator) and the current orientation.
 */

/** Safe-area insets (CSS px) from `env(safe-area-inset-*)`; 0 on a desktop browser. */
export interface SafeAreaInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const NO_INSETS: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/** A rectangle in game-pixel space. */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type Orientation = 'portrait' | 'landscape';

/** A 9-point anchor for edge/center-relative placement. */
export type Anchor =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'left'
  | 'center'
  | 'right'
  | 'bottom-left'
  | 'bottom'
  | 'bottom-right';

/** A full immutable snapshot of the current responsive layout. */
export interface LayoutSnapshot {
  readonly width: number;
  readonly height: number;
  readonly insets: SafeAreaInsets;
  readonly orientation: Orientation;
  /** Width >= 820 reads as a roomy/desktop layout; below is compact/phone. */
  readonly isCompact: boolean;
  /** The content rectangle inside the safe-area insets. */
  readonly safe: Rect;
}

/** True when the viewport is taller than it is wide. */
export function orientationOf(width: number, height: number): Orientation {
  return height >= width ? 'portrait' : 'landscape';
}

/** Breakpoint: below this width we use the compact (phone) layout. */
export const COMPACT_MAX_WIDTH = 820;

export function isCompactWidth(width: number): boolean {
  return width < COMPACT_MAX_WIDTH;
}

/** The drawable content rect once the safe-area insets are subtracted. */
export function safeRect(width: number, height: number, insets: SafeAreaInsets): Rect {
  const x = Math.max(0, insets.left);
  const y = Math.max(0, insets.top);
  const w = Math.max(0, width - insets.left - insets.right);
  const h = Math.max(0, height - insets.top - insets.bottom);
  return { x, y, width: w, height: h };
}

/** Build the immutable snapshot from a raw size + insets. */
export function makeSnapshot(
  width: number,
  height: number,
  insets: SafeAreaInsets = NO_INSETS,
): LayoutSnapshot {
  return {
    width,
    height,
    insets,
    orientation: orientationOf(width, height),
    isCompact: isCompactWidth(width),
    safe: safeRect(width, height, insets),
  };
}

/**
 * The (x, y) for placing an object of `size` at an `anchor` of `rect`, with an
 * optional `pad` margin from the edges. The returned point is the object's CENTER
 * (Phaser GameObjects default to centered origins in the UI kit), so a control
 * placed at 'bottom-right' sits fully inside the rect with `pad` to the edges.
 */
export function anchorPoint(
  rect: Rect,
  anchor: Anchor,
  size: { readonly width: number; readonly height: number } = { width: 0, height: 0 },
  pad = 0,
): { x: number; y: number } {
  const halfW = size.width / 2;
  const halfH = size.height / 2;
  const left = rect.x + pad + halfW;
  const right = rect.x + rect.width - pad - halfW;
  const cx = rect.x + rect.width / 2;
  const top = rect.y + pad + halfH;
  const bottom = rect.y + rect.height - pad - halfH;
  const cy = rect.y + rect.height / 2;

  switch (anchor) {
    case 'top-left':
      return { x: left, y: top };
    case 'top':
      return { x: cx, y: top };
    case 'top-right':
      return { x: right, y: top };
    case 'left':
      return { x: left, y: cy };
    case 'center':
      return { x: cx, y: cy };
    case 'right':
      return { x: right, y: cy };
    case 'bottom-left':
      return { x: left, y: bottom };
    case 'bottom':
      return { x: cx, y: bottom };
    case 'bottom-right':
      return { x: right, y: bottom };
  }
}

/**
 * A centered rect of `width` x `height` clamped to fit inside `outer` (minus `pad`).
 * Used to lay out modal panels that stay within the safe content area on any screen.
 */
export function centeredBox(outer: Rect, width: number, height: number, pad = 0): Rect {
  const maxW = Math.max(0, outer.width - pad * 2);
  const maxH = Math.max(0, outer.height - pad * 2);
  const w = Math.min(width, maxW);
  const h = Math.min(height, maxH);
  return {
    x: outer.x + (outer.width - w) / 2,
    y: outer.y + (outer.height - h) / 2,
    width: w,
    height: h,
  };
}

/**
 * Split a rect into `n` equal columns (gutter between each). Returns the column rects.
 * Handy for responsive grids (team slots, dex tiles) that reflow phone <-> desktop.
 */
export function columns(rect: Rect, n: number, gutter = 0): Rect[] {
  if (n <= 0) return [];
  const totalGutter = gutter * (n - 1);
  const colW = Math.max(0, (rect.width - totalGutter) / n);
  const out: Rect[] = [];
  for (let i = 0; i < n; i++) {
    out.push({
      x: rect.x + i * (colW + gutter),
      y: rect.y,
      width: colW,
      height: rect.height,
    });
  }
  return out;
}

/**
 * Choose a column count that keeps tiles near a target width — the core of a
 * responsive grid that shows more columns on desktop, fewer on a phone.
 */
export function responsiveColumnCount(
  availableWidth: number,
  targetTileWidth: number,
  min = 1,
  max = 12,
): number {
  const raw = Math.floor(availableWidth / Math.max(1, targetTileWidth));
  return Math.max(min, Math.min(max, raw || min));
}
